import React, { useEffect, useMemo, useState } from 'react';
import WithSection from '../../components/WithSection/WithSection';
import { graphql, Link } from 'gatsby';
import Box from '../../components/Box/Box';
import {
    Body1,
    Body2,
    Caption,
    Head1,
    Head2,
    Head4,
    Headline,
} from '../../components/Typography/Typography';
import { rem } from 'polished';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../components/Button/Button';
import { theme } from '../../../config/theme';
import Field from '../../components/Field/Field';
import Divider from '../../components/Divider/Divider';
import { abbreviateNumber } from '../../utils/abbreviateNumber';
import PrismicLink from '../../components/PrismicLink/PrismicLink';

const ResultCard = ({
    title,
    description,
    result,
    isPercentage = false,
    wordAfterForwardSlash,
}) => {
    return (
        <Box
            borderRadius="sm"
            border={`1px solid ${theme.colors.primary['1400']}`}
            py={rem(8)}
            px={rem(16)}
        >
            <Body2 mb={rem(4)}>{title}</Body2>
            <Head4 mb={rem(8)} mt={rem(4)} color="primary.1400">
                {isPercentage ? '' : '$'}
                {result}
                {isPercentage ? '%' : ''}{' '}
                <Box as="span" fontSize="50%" fontFamily="body">
                    /{wordAfterForwardSlash}
                </Box>
            </Head4>
            <Caption color="neutral.1400">{description}</Caption>
        </Box>
    );
};

const RangeRow = ({ control, setValue, getValues, name }) => {
    return (
        <Box display="flex" gap={rem(16)} alignItems="center" mt={rem(4)}>
            <Box flex={1}>
                <Controller
                    defaultValue={[0, 0]}
                    name={name}
                    control={control}
                    as={Field.InputRange}
                    // @ts-ignore
                    onInput={(value) => {
                        setValue(name, value);
                    }}
                />
            </Box>
            <Box maxWidth={rem(70)}>
                <Field id="id">
                    <Field.Input
                        value={getValues(name)?.[1]}
                        type="text"
                        endText={
                            <Caption bold color="neutral.1500">
                                %
                            </Caption>
                        }
                        onChange={(event) => {
                            const value = String(event.target.value).replace(
                                /[^0-9]/g,
                                ''
                            );
                            const valueBetween0To100 =
                                value === ''
                                    ? ''
                                    : Math.min(
                                          Math.max(parseInt(value, 10), 0),
                                          100
                                      );

                            setValue(name, [0, valueBetween0To100]);
                        }}
                    />
                </Field>
            </Box>
        </Box>
    );
};

const CostCalcInputField = ({
    setValue,
    labelText,
    inputProps,
    labelProps = {},
}) => {
    return (
        <Field id={inputProps.name}>
            <Field.Label {...labelProps}>{labelText}</Field.Label>
            <Field.Input
                placeholder="0"
                type="text"
                {...inputProps}
                onChange={(event) => {
                    const valueWithOnlyNumbers: string =
                        event.target.value.replace(/[^0-9]/g, '');

                    setValue(inputProps.name, valueWithOnlyNumbers);
                }}
            />
        </Field>
    );
};

const CostCalculatorSection = ({
    data,
    styling: { backgroundVariant: sectionBackground },
}) => {
    const {
        primary: {
            cost_calc_section_description,
            input_column_heading,
            meetings_evaluation_row_description,
            meetings_evaluation_row_title,
            meetings_row_description,
            meetings_row_title,
            participants_row_description,
            participants_row_title,
            result_column_heading,
            cost_calc_section_title,
            cost_calc_inputs,
            evaluation_range_1_description,
            evaluation_range_1_title,
            evaluation_range_2_description,
            evaluation_range_2_title,
            evaluation_range_3_description,
            evaluation_range_3_title,
            evaluation_range_4_description,
            evaluation_range_4_title,
            evaluation_range_5_description,
            evaluation_range_5_title,
            result_card_1_after_slash_word,
            result_card_1_title,
            result_card_1_description,
            result_card_2_after_slash_word,
            result_card_2_title,
            result_card_2_description,
            result_card_3_after_slash_word,
            result_card_3_description,
            result_card_3_title,
            result_card_4_after_slash_word,
            result_card_4_description,
            result_card_4_title,
            total_cost_after_slash_word,
            cta_1_link,
            cta_1_text,
            cta_2_link,
            cta_2_text,
        },
    } = data;
    let costCalcInputs = {};
    cost_calc_inputs.document.data.fields.forEach((obj) => {
        let key = obj.key;
        let { key: _, ...rest } = obj; // Using destructuring to omit the 'key' property
        costCalcInputs[key] = rest;
    });

    const { control, register, watch, setValue, getValues } = useForm();
    const values = watch();
    const {
        topManagementLevel = 0,
        middleManagementLevel = 0,
        lowManagementLevel = 0,
        employeesCount = 0,
        topManagementLevelAverageSalary = 0,
        middleManagementLevelAverageSalary = 0,
        lowManagementLevelAverageSalary = 0,
        employeesAverageSalary = 0,
        meetingTimeInHours = 0,
        meetingsCountPerWeek = 0,
        range_1 = [0, 0],
        range_2 = [0, 0],
        range_3 = [0, 0],
        range_4 = [0, 0],
        range_5 = [0, 0],
    } = values;

    const WORKING_HOURS_PER_MONTH = 160;
    const SALARIES_SUM_TOTAL_BY_LEVEL = {
        top: +topManagementLevel * +topManagementLevelAverageSalary,
        middle: +middleManagementLevel * +middleManagementLevelAverageSalary,
        low: +lowManagementLevel * +lowManagementLevelAverageSalary,
        employees: +employeesCount * +employeesAverageSalary,
    };
    const totalCombinedSalaries = Object.values(
        SALARIES_SUM_TOTAL_BY_LEVEL
    ).reduce((sum, val) => sum + val, 0);
    const singleMeetingCostPerHour =
        +totalCombinedSalaries / WORKING_HOURS_PER_MONTH;
    const singleMeetingCost = singleMeetingCostPerHour * +meetingTimeInHours;

    const totalMeetingHoursPerWeek =
        +meetingTimeInHours * +meetingsCountPerWeek;
    const totalMeetingCostPerWeek =
        singleMeetingCostPerHour * totalMeetingHoursPerWeek;

    const meetingEffectivenessScore =
        (range_1?.[1] +
            range_2?.[1] +
            range_3?.[1] +
            range_4?.[1] +
            range_5?.[1]) /
        5;
    const investmentWaste =
        (meetingEffectivenessScore &&
            totalMeetingCostPerWeek *
                ((100 - meetingEffectivenessScore) / 100)) ||
        0;
    const meetingROI =
        totalMeetingCostPerWeek * (meetingEffectivenessScore / 100);

    const evaluationsRanges = [
        [evaluation_range_1_title, evaluation_range_1_description],
        [evaluation_range_2_title, evaluation_range_2_description],
        [evaluation_range_3_title, evaluation_range_3_description],
        [evaluation_range_4_title, evaluation_range_4_description],
        [evaluation_range_5_title, evaluation_range_5_description],
    ];

    return (
        <>
            <Box
                gridColumn={{ _: '1/-1', lg: '2/-2' }}
                display="flex"
                flexDirection="column"
                justifyItems="center"
                textAlign="center"
                mb={rem(40)}
            >
                <Head2 bold color="neutral.white">
                    {cost_calc_section_title.text}
                </Head2>

                <Body1
                    mt="20px"
                    color="neutral.white"
                    maxWidth={rem(557)}
                    marginX="auto"
                >
                    {cost_calc_section_description}
                </Body1>
            </Box>
            <Box
                display="grid"
                gridColumn={{ _: '1/-1', lg: '2/-2', xl: '3/-3' }}
                gridTemplateColumns={{ _: '1fr', md: '1fr 1fr' }}
                gridGap={rem(32)}
                alignItems="start"
            >
                <Box
                    backgroundColor="neutral.white"
                    borderRadius="sm"
                    py={rem(24)}
                    px={rem(32)}
                >
                    <Head4 mb={rem(16)}>{input_column_heading}</Head4>
                    <Box>
                        <Headline mb={rem(4)} fontWeight="500">
                            {participants_row_title}
                        </Headline>
                        <Caption color="neutral.1400">
                            {participants_row_description}
                        </Caption>
                        <Box
                            mt={rem(16)}
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            gridGap={rem(12)}
                        >
                            <Box
                                display="flex"
                                flexDirection="column"
                                overflow="hidden"
                                gap={rem(12)}
                            >
                                {[
                                    'topManagementLevel',
                                    'middleManagementLevel',
                                    'lowManagementLevel',
                                    'employeesCount',
                                ].map((key) => (
                                    <CostCalcInputField
                                        key={key}
                                        labelText={costCalcInputs[key].label}
                                        setValue={setValue}
                                        inputProps={{
                                            defaultValue: '',
                                            name: key,
                                            ref: register(),
                                            placeholder:
                                                costCalcInputs[key]
                                                    .placeholder || 0,
                                        }}
                                    />
                                ))}
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                overflow="hidden"
                                gap={rem(12)}
                            >
                                {[
                                    'topManagementLevelAverageSalary',
                                    'middleManagementLevelAverageSalary',
                                    'lowManagementLevelAverageSalary',
                                    'employeesAverageSalary',
                                ].map((key) => (
                                    <CostCalcInputField
                                        key={key}
                                        labelText={costCalcInputs[key].label}
                                        setValue={setValue}
                                        inputProps={{
                                            placeholder:
                                                costCalcInputs[key]
                                                    .placeholder || 0,
                                            defaultValue: '',
                                            name: key,
                                            ref: register(),
                                            startIcon: (
                                                <Box
                                                    as={Caption}
                                                    width={rem(16)}
                                                    textAlign="center"
                                                    bold
                                                    color="neutral.1500"
                                                >
                                                    $
                                                </Box>
                                            ),
                                        }}
                                        labelProps={{
                                            tooltipLabel:
                                                costCalcInputs[key]
                                                    .label_tooltip,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                    <Divider
                        color={theme.colors.neutral[500]}
                        verticalOffset="24px"
                    />
                    <Box>
                        <Headline mb={rem(4)} fontWeight="500">
                            {meetings_row_title}
                        </Headline>
                        <Caption color="neutral.1400">
                            {meetings_row_description}
                        </Caption>
                        <Box
                            mt={rem(16)}
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            gridGap={rem(12)}
                        >
                            <Field id="meetingTimeInHours">
                                <Field.Label
                                    toolTipLabel={
                                        costCalcInputs['meetingTimeInHours']
                                            .label_tooltip
                                    }
                                >
                                    {costCalcInputs['meetingTimeInHours'].label}
                                </Field.Label>
                                <Field.QuantityInput
                                    placeholder="0"
                                    defaultValue=""
                                    name="meetingTimeInHours"
                                    ref={register({ required: true })}
                                    onChange={(event) => {
                                        const valueWithOnlyNumbers: string =
                                            event.target.value.replace(
                                                /[^0-9.]/g,
                                                ''
                                            );

                                        setValue(
                                            'meetingTimeInHours',
                                            valueWithOnlyNumbers
                                        );
                                    }}
                                    increment={() => {
                                        setValue(
                                            'meetingTimeInHours',
                                            String(
                                                parseFloat(
                                                    (
                                                        Number(
                                                            values.meetingTimeInHours
                                                        ) + 0.5
                                                    ).toFixed(2)
                                                )
                                            )
                                        );
                                    }}
                                    decrement={() => {
                                        if (
                                            Number(values.meetingTimeInHours) <
                                            0.5
                                        ) {
                                            setValue('meetingTimeInHours', '0');
                                            return;
                                        }
                                        setValue(
                                            'meetingTimeInHours',
                                            String(
                                                parseFloat(
                                                    (
                                                        Number(
                                                            values.meetingTimeInHours
                                                        ) - 0.5
                                                    ).toFixed(2)
                                                )
                                            )
                                        );
                                    }}
                                />
                            </Field>
                            <Field id="meetingsCountPerWeek">
                                <Field.Label
                                    toolTipLabel={
                                        costCalcInputs['meetingsCountPerWeek']
                                            .label_tooltip
                                    }
                                >
                                    {
                                        costCalcInputs['meetingsCountPerWeek']
                                            .label
                                    }
                                </Field.Label>
                                <Field.QuantityInput
                                    placeholder={
                                        costCalcInputs['meetingsCountPerWeek']
                                            .placeholder || 0
                                    }
                                    defaultValue="1"
                                    name="meetingsCountPerWeek"
                                    ref={register({ required: true })}
                                    onChange={(event) => {
                                        const valueWithOnlyNumbers: string =
                                            event.target.value.replace(
                                                /[^0-9]/g,
                                                ''
                                            );

                                        setValue(
                                            'meetingsCountPerWeek',
                                            valueWithOnlyNumbers
                                        );
                                    }}
                                    increment={() => {
                                        setValue(
                                            'meetingsCountPerWeek',
                                            String(
                                                Number(
                                                    values.meetingsCountPerWeek
                                                ) + 1
                                            )
                                        );
                                    }}
                                    decrement={() => {
                                        if (
                                            Number(
                                                values.meetingsCountPerWeek
                                            ) === 0
                                        )
                                            return;
                                        setValue(
                                            'meetingsCountPerWeek',
                                            String(
                                                Number(
                                                    values.meetingsCountPerWeek
                                                ) - 1
                                            )
                                        );
                                    }}
                                />
                            </Field>
                        </Box>
                    </Box>
                    <Divider
                        color={theme.colors.neutral[500]}
                        verticalOffset="24px"
                    />
                    <Box>
                        <Headline mb={rem(4)} fontWeight="500">
                            {meetings_evaluation_row_title}
                        </Headline>
                        <Caption color="neutral.1400">
                            {meetings_evaluation_row_description}
                        </Caption>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={rem(12)}
                            mt={rem(16)}
                        >
                            {evaluationsRanges.map(
                                ([title, description], i) => (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={rem(4)}
                                    >
                                        <Caption>{title}</Caption>
                                        <Caption color="neutral.1400">
                                            {description}
                                        </Caption>
                                        <RangeRow
                                            name={`range_${i + 1}`}
                                            control={control}
                                            setValue={setValue}
                                            getValues={getValues}
                                        />
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box
                    backgroundColor="neutral.white"
                    borderRadius="sm"
                    py={rem(24)}
                    px={rem(32)}
                    position="sticky"
                    top={`calc(var(--header-height) + 30px)`}
                >
                    <Head4>{result_column_heading}</Head4>
                    <Head1 mt={rem(4)} mb={rem(8)} color="primary.1000">
                        {meetingEffectivenessScore}
                        {'% '}
                        <Box as="span" fontSize="50%">
                            {total_cost_after_slash_word
                                ? `/${total_cost_after_slash_word}`
                                : ''}
                        </Box>
                    </Head1>
                    <Body2 color="neutral.1400">
                        For {topManagementLevel || 0} top management level,{' '}
                        {middleManagementLevel || 0} middle management level,{' '}
                        {lowManagementLevel || 0} low management level, and{' '}
                        {employeesCount || 0} employees to meet on average{' '}
                        {meetingsCountPerWeek || 0} times a week for{' '}
                        {meetingTimeInHours || 0} hours.{' '}
                    </Body2>
                    <Box
                        display="flex"
                        flexDirection="column"
                        mt={rem(16)}
                        gap={rem(12)}
                    >
                        <ResultCard
                            title={result_card_1_title}
                            description={result_card_1_description}
                            wordAfterForwardSlash={
                                result_card_1_after_slash_word
                            }
                            result={abbreviateNumber(totalMeetingCostPerWeek)}
                        />
                        <ResultCard
                            title={result_card_2_title}
                            description={result_card_2_description}
                            wordAfterForwardSlash={
                                result_card_2_after_slash_word
                            }
                            result={abbreviateNumber(meetingROI)}
                        />
                        <ResultCard
                            title={result_card_3_title}
                            description={result_card_3_description}
                            wordAfterForwardSlash={
                                result_card_3_after_slash_word
                            }
                            result={abbreviateNumber(investmentWaste)}
                        />
                        <ResultCard
                            title={result_card_4_title}
                            description={result_card_4_description}
                            wordAfterForwardSlash={
                                result_card_4_after_slash_word
                            }
                            result={abbreviateNumber(singleMeetingCost)}
                        />
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={rem(8)}
                        mt={rem(16)}
                    >
                        {cta_1_text && (
                            <PrismicLink link={cta_1_link} tabIndex={-1}>
                                <Button size="lg" fullWidth>
                                    {cta_1_text}
                                </Button>
                            </PrismicLink>
                        )}

                        {cta_2_text && (
                            <PrismicLink link={cta_2_link} tabIndex={-1}>
                                <Button size="lg" fullWidth variant="secondary">
                                    {cta_2_text}
                                </Button>
                            </PrismicLink>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default WithSection(CostCalculatorSection);

export const _ = graphql`
    fragment COST_CALCULATOR_SECTION on PrismicSectionDataBodyCostCalculator {
        id
        primary {
            cost_calc_section_description
            input_column_heading
            meetings_evaluation_row_description
            meetings_evaluation_row_title
            meetings_row_description
            meetings_row_title
            participants_row_description
            participants_row_title
            result_column_heading
            cost_calc_section_title {
                text
            }
            evaluation_range_1_description
            evaluation_range_1_title
            evaluation_range_2_description
            evaluation_range_2_title
            evaluation_range_3_description
            evaluation_range_3_title
            evaluation_range_4_description
            evaluation_range_4_title
            evaluation_range_5_description
            evaluation_range_5_title
            result_card_1_after_slash_word
            result_card_1_title
            result_card_1_description
            result_card_2_after_slash_word
            result_card_2_title
            result_card_2_description
            result_card_3_after_slash_word
            result_card_3_description
            result_card_3_title
            result_card_4_after_slash_word
            result_card_4_description
            result_card_4_title
            total_cost_after_slash_word
            cta_1_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_1_text
            cta_2_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_2_text
            cost_calc_inputs {
                document {
                    ... on PrismicDynamicForm {
                        id
                        data {
                            fields {
                                key
                                label
                                label_tooltip
                                placeholder
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;
